import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Stack, Typography } from '@mui/material';
import { type ChangeEvent, memo, type ReactNode } from 'react';
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { FormSwitch } from 'src/components/Forms';
import {
  PlanAccordionDetails,
  PlanAccordionSummary,
  PlanAccordionWrapper,
  planControlFormSx,
  planHeaderToggleFormSx,
  planHeaderToggleLabelSx,
  PlanName,
  planToggleLabelSx,
  planToggleSx,
} from 'src/pages/Settings/memberships/components/plans-tab/styled';
import {
  PlansTabField,
  PlansTabFormValues,
  PlansTabPlanValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';
import PlanPriceField from './plan-price-field';

interface PlanAccordionProps {
  index: number;
  planId: string;
  expanded: boolean;
  onExpandedChange: (planId: string, expanded: boolean) => void;
  readOnly: boolean;
  // commented for future use
  // onRemove: () => void;
  // onMoveUp?: () => void;
  // onMoveDown?: () => void;
}

type PlanFieldPath = FieldPath<PlansTabFormValues>;

const getPlanFieldName = (index: number, field: keyof PlansTabPlanValues): PlanFieldPath =>
  `${PlansTabField.Plans}.${index}.${field}` as PlanFieldPath;

interface PlanSwitchFieldProps {
  name: string;
  label: ReactNode;
  ariaLabel: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  sx?: Parameters<typeof FormSwitch>[0]['sx'];
  labelSx?: Parameters<typeof FormSwitch>[0]['labelSx'];
  textSx?: Parameters<typeof Typography>[0]['sx'];
}

const PlanSwitchField = ({
  name,
  label,
  ariaLabel,
  onChange,
  disabled = false,
  sx = planControlFormSx,
  labelSx = planToggleLabelSx,
  textSx = planToggleSx,
}: PlanSwitchFieldProps): React.JSX.Element => (
  <FormSwitch
    type="switch"
    name={name}
    label={<Typography sx={textSx}>{label}</Typography>}
    labelPlacement="start"
    sx={sx}
    labelSx={labelSx}
    switchFieldProps={{
      size: 'small',
      sx: planToggleSx,
      inputProps: { 'aria-label': ariaLabel },
    }}
    onChange={onChange}
    disabled={disabled}
  />
);

export const PlanAccordion = ({
  index,
  planId,
  expanded,
  onExpandedChange,
  readOnly,
  // commented for future use
  // onRemove,
  // onMoveUp,
  // onMoveDown,
}: PlanAccordionProps): React.JSX.Element => {
  const { control, getValues, setValue } = useFormContext<PlansTabFormValues>();

  const planValues = useWatch({
    control,
    name: `${PlansTabField.Plans}.${index}`,
  }) as PlansTabPlanValues | undefined;

  const billingOptions = useWatch({
    control,
    name: PlansTabField.BillingOptions,
  }) as PlansTabFormValues['billingOptions'] | undefined;

  const planName = planValues?.name ?? 'Plan';
  const isEnabled = planValues?.enabled ?? false;
  const callForPricing = planValues?.callForPricing ?? false;
  const monthlyEnabled = billingOptions?.monthlyEnabled ?? false;
  const annualEnabled = billingOptions?.annualEnabled ?? false;
  const showPriceFields = isEnabled && !callForPricing;

  const handleMostPopularChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (readOnly) {
      return;
    }

    if (!event.target.checked) {
      return;
    }

    const plans = getValues(PlansTabField.Plans);

    if (!Array.isArray(plans)) {
      return;
    }

    plans.forEach((plan, planIndex) => {
      if (planIndex === index || !plan?.mostPopular) {
        return;
      }

      setValue(getPlanFieldName(planIndex, 'mostPopular'), false, {
        shouldDirty: true,
        shouldTouch: true,
      });
    });
  };

  return (
    <PlanAccordionWrapper
      elevation={0}
      disableGutters
      expanded={isEnabled ? expanded : false}
      onChange={(_, expandedValue) => {
        if (!isEnabled) {
          return;
        }
        onExpandedChange(planId, expandedValue);
      }}
      data-testid={`plan-accordion-${planId}`}
    >
      <PlanAccordionSummary
        data-disabled={!isEnabled}
        expandIcon={isEnabled ? <ChevronRightIcon /> : null}
      >
        <PlanName
          variant="body2"
          sx={{
            color: isEnabled ? 'text.primary' : 'text.disabled',
          }}
        >
          {planName}
        </PlanName>
        <Stack
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <PlanSwitchField
            name={getPlanFieldName(index, 'enabled')}
            label=""
            ariaLabel="Plan active toggle"
            disabled={readOnly}
            onChange={(event) => {
              event.stopPropagation();
            }}
            sx={planHeaderToggleFormSx}
            labelSx={planHeaderToggleLabelSx}
          />
        </Stack>
        {/* commented for future use
         <Stack direction="row" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            aria-label="Move plan up"
            disabled={!onMoveUp}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onMoveUp?.();
            }}
          >
            <ArrowUpwardIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Move plan down"
            disabled={!onMoveDown}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onMoveDown?.();
            }}
          >
            <ArrowDownwardIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Delete plan"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
          >
            <DeleteOutlineIcon fontSize="inherit" />
          </IconButton>
        </Stack> */}
      </PlanAccordionSummary>

      {isEnabled && expanded ? (
        <PlanAccordionDetails>
          <Stack>
            {/* commented for future use
            <FormTextField
              type="textfield"
              name={getPlanFieldName(index, 'name')}
              label="Plan name"
              textFieldProps={{
                variant: 'outlined',
                size: 'small',
                fullWidth: true,
              }}
            /> */}

            <PlanSwitchField
              name={getPlanFieldName(index, 'mostPopular')}
              label="Most popular"
              ariaLabel="Most popular toggle"
              onChange={handleMostPopularChange}
              disabled={readOnly}
            />

            <PlanSwitchField
              name={getPlanFieldName(index, 'callForPricing')}
              label="Call for pricing"
              ariaLabel="Call for pricing toggle"
              disabled={readOnly}
            />

            {showPriceFields ? (
              <Stack gap={2} width="100%" mt={2}>
                {monthlyEnabled ? (
                  <PlanPriceField
                    name={getPlanFieldName(index, 'monthlyPrice')}
                    label="Monthly price"
                    endAdornment="/mo"
                    isRequired={monthlyEnabled}
                    disabled={readOnly}
                  />
                ) : null}

                {annualEnabled ? (
                  <PlanPriceField
                    name={getPlanFieldName(index, 'annualPrice')}
                    label="Annual price"
                    endAdornment="/yr"
                    isRequired={annualEnabled}
                    disabled={readOnly}
                  />
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        </PlanAccordionDetails>
      ) : null}
    </PlanAccordionWrapper>
  );
};

export const MemoPlanAccordion = memo(PlanAccordion);
